/**
 * The weight, typed on a pad the sheet supplies itself.
 *
 * A number field would open the system keyboard, and the system keyboard is
 * 292px on the phone this is designed for. A 560px sheet plus 292px of keyboard
 * is 852px on an 812px screen, so the sheet would have to give up the list to
 * make room — and the list is the thing you came back to when you wanted to add
 * a second item. One tap becomes dismiss, re-focus, scroll.
 *
 * So grams are typed here and never anywhere else. Twelve keys, an `Add` inside
 * the pad rather than below it, and the food list still visible above. The
 * governing rule the whole sheet follows: **one surface, one input at a time**.
 * The system keyboard is for words and multi-field forms — search, "Fix",
 * entering a food by hand — and opening either dismisses the other, so the
 * sheet is never taller than the screen.
 *
 * The value is rendered text, not a focused input. An `<input readOnly>` still
 * raises the keyboard on some Android browsers when tapped, and `inputMode` is
 * a hint rather than a guarantee — the only arrangement with nothing to get
 * wrong is not to have a focusable field at all. Keyboard users are served by
 * the pad's own buttons, which are buttons, and by typing being unnecessary.
 *
 * Held as text rather than as a number, so a half-typed "1" on the way to "180"
 * is not a 1 g portion, and "60." is a thing somebody is still writing.
 *
 * The sheet raised it first, but the argument is about weights and not about
 * sheets: re-weighing a row already on Today is the same gesture with the same
 * keyboard problem, so it types on the same pad and commits on the same key.
 */

import { forGrams, formatGrams, parseDecimal, type Macros, type PortionChip } from "@calorio/core";
import { useRef } from "react";

interface GramsPadProps {
  /** What is being weighed. Ellipsised — the number is the important half. */
  name: string;
  /** The buffer, as typed. */
  value: string;
  onChange: (next: string) => void;
  unit: string;
  per100: Macros;
  /**
   * Portions worth one tap instead of three.
   *
   * What the product says a portion is, the weights this person keeps logging
   * it at, the round numbers — most portions need no typing at all, and the pad
   * is the fallback rather than the point. Built by `suggestedPortions` in
   * `@calorio/core`, so the phone's pad and this one agree about the row.
   *
   * Tapped again, a chip adds: two slices is the chip twice rather than the
   * chip and then arithmetic. The chip counts what it has put in, `⌫` takes
   * one back out, and a tap that arrives inside a double-tap of the last one
   * is dropped — the three together are what make adding safe to offer.
   */
  chips?: PortionChip[];
  /**
   * Whether tapping a chip twice means two of it.
   *
   * True wherever a chip is a portion, which is everywhere a food is being
   * weighed. False where the number is a measurement rather than a helping:
   * the pot's "Raw 850 g" is what the ingredients weighed before the water
   * went, and there is no sense in which the pan holds two of them — that chip
   * is a reading to copy, so it copies.
   */
  chipsAdd?: boolean;
  /**
   * What saving will do, when it is not going to add a row.
   *
   * Shown where the weight is being typed, because that is where the
   * expectation forms — a line further up the sheet would be read after the
   * fact, which is too late to be a warning about anything.
   */
  note?: string;
  /**
   * What the number buys, when it does not buy a portion of a food.
   *
   * The pad's readout is normally the calories in this weight, because that is
   * what a weight means everywhere a weight is typed — except once. Weighing
   * the pot after cooking is a measurement about the *pan*: the calories are
   * already fixed and what the number changes is the density they are spread
   * across. So the caller supplies the three figures that do move, and the kcal
   * cell steps aside rather than printing a number about nothing.
   */
  figures?: { label: string; value: string }[];
  /** Usually "Add"; the confirm state says the same thing about a scanned food. */
  action?: string;
  disabled?: boolean;
  onCommit: () => void;
}

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0"] as const;

/** How many digits a weight may have. Past four the pad is taking keystrokes
    it will never show a plausible weight for. */
const MAX_DIGITS = 4;

/**
 * How close together two taps of one chip have to be to be the same tap.
 *
 * Chips add, so a tap that bounces is a second helping nobody asked for. The
 * window is the platform's own double-tap threshold: a deliberate "and another
 * one" lands well outside it, a thumb that lifts badly or a click the hardware
 * repeated lands well inside. Measured from the last tap that *counted* rather
 * than the last one seen, so three slices tapped in a row are still three
 * slices — only the bounce off each one is swallowed.
 */
const DOUBLE_TAP_MS = 350;

/** The weight a base and a stack of chips add up to, as the pad prints it. */
const totalOf = (base: number, chips: readonly number[]) =>
  formatGrams(chips.reduce((sum, grams) => sum + grams, base));

export function GramsPad({
  name,
  value,
  onChange,
  unit,
  per100,
  chips,
  chipsAdd = true,
  note,
  figures,
  action = "Add",
  disabled = false,
  onCommit,
}: GramsPadProps) {
  const quantity = parseDecimal(value);
  const ready = Number.isFinite(quantity) && quantity > 0;
  const kcal = ready ? Math.round(forGrams(per100, quantity).kcal) : null;

  /**
   * Whether the number on the pad is a suggestion nobody has touched.
   *
   * The design asks for the prefill to be "selected on open so the first
   * keypress replaces it", which is what a text field would give for free. This
   * is not a text field — that is the whole point of it — so the selection has
   * to be modelled, and without it the pad appended: opening on a suggested
   * 100 and typing 8 gave 1008, then refused the next key for being too long.
   * Reaching 180 meant pressing delete twice first, on a pad built so that
   * common portions need no typing at all.
   *
   * A chip sets it again, because tapping "100 g" and then typing is the same
   * gesture as arriving on a suggestion and typing: you are replacing it.
   */
  const fresh = useRef(true);

  /**
   * The weight the chips are adding to, and the chips that have been added.
   *
   * A chip used to replace whatever was there, which made two slices a thing
   * you did arithmetic for: tap 32, double it in your head, backspace twice,
   * type 64. Tapping again is the gesture people already reach for, so it
   * adds — and the risk that creates is a tap the user did not mean, so the
   * pad keeps the receipt. `⌫` on 64 g of bread built from two chips takes
   * back the second slice and leaves 32, rather than deleting a digit and
   * leaving 6.
   */
  const built = useRef<{ base: number; chips: number[] }>({ base: 0, chips: [] });

  /** The last chip tap that counted, so a bounce can be told from a helping. */
  const lastTap = useRef({ grams: 0, at: 0 });

  /**
   * The receipt, or an empty one where it no longer describes the number.
   *
   * The pad is reused rather than remounted — the sheet swaps the food under
   * it, the editor swaps the row — so a stack left over from the last weight
   * would offer to un-add a chip this number never had. Checked against the
   * value it claims to have built, and printed the same way, so the two are
   * comparable as text and there is no tolerance to get wrong.
   */
  const receipt = (): { base: number; chips: number[] } => {
    const stack = built.current;
    if (stack.chips.length === 0) return { base: 0, chips: [] };
    return totalOf(stack.base, stack.chips) === value ? stack : { base: 0, chips: [] };
  };

  /**
   * Typing replaces a suggestion and appends to anything else, with two
   * exceptions that would otherwise make a number the user cannot see the end
   * of.
   *
   * A second decimal point is dropped rather than refused, because the key
   * doing nothing is the same message with no error in it. And four digits is
   * more than any portion: past that the pad is taking keystrokes it will never
   * show a plausible weight for.
   */
  const press = (key: string) => {
    // Typed digits are nobody's stack of chips: what `⌫` takes back from here
    // on is a digit, which is what a pad of digits promises.
    built.current = { base: 0, chips: [] };
    if (fresh.current) {
      fresh.current = false;
      // A leading "." is the start of "0.5", not a replacement of nothing.
      if (key !== ".") return onChange(key);
    }
    if (key === "." && value.includes(".")) return;
    if (value.replace(".", "").length >= MAX_DIGITS && key !== ".") return;
    onChange(value + key);
  };

  /**
   * A portion, laid on top of the weight rather than over it.
   *
   * Replacing is right exactly once: on a number the user has not touched, a
   * prefilled 100 or the weight a row was logged at. Arriving on that and
   * tapping "30 g" is asking for 30 g, not for 130. Everything after it — a
   * second chip, a chip on a weight somebody typed — is another helping.
   */
  const tapChip = (chip: PortionChip) => {
    const now = Date.now();
    if (chip.grams === lastTap.current.grams && now - lastTap.current.at < DOUBLE_TAP_MS) return;

    const stack = receipt();
    const replacing = !chipsAdd || (fresh.current && stack.chips.length === 0);
    const typed = parseDecimal(value);
    const base = replacing ? 0 : stack.chips.length > 0 ? stack.base : typed > 0 ? typed : 0;
    const chips = replacing ? [chip.grams] : [...stack.chips, chip.grams];
    const printed = totalOf(base, chips);
    // Refused the way the digits are refused: the key does nothing, and that
    // is the same message with no error in it.
    if (printed.replace(".", "").length > MAX_DIGITS) return;

    lastTap.current = { grams: chip.grams, at: now };
    // A chip that only ever copies leaves no receipt, so `⌫` stays a backspace
    // on a number that was never built a tap at a time.
    built.current = chipsAdd ? { base, chips } : { base: 0, chips: [] };
    // A chip is an answer, so what follows it is a new one.
    fresh.current = true;
    onChange(printed);
  };

  /**
   * Back one step, where a step is whatever the last one was.
   *
   * The chips are what makes this more than a backspace. A weight built by
   * tapping is undone by untapping — one key, straight back to what it said a
   * tap ago — and a weight that was typed loses a digit, as it always did.
   */
  const back = () => {
    const stack = receipt();
    if (stack.chips.length > 0) {
      const chips = stack.chips.slice(0, -1);
      built.current = { base: stack.base, chips };
      // The tap that was taken back cannot also be the tap the next one
      // bounced off, or fixing a mis-tap would block the retap that follows.
      lastTap.current = { grams: 0, at: 0 };
      // What is left is a chip's answer, or the number that was there before.
      fresh.current = chips.length > 0;
      if (chips.length === 0 && stack.base === 0) return onChange("");
      return onChange(totalOf(stack.base, chips));
    }
    // Deleting is editing, so what is left is the user's, not a suggestion to
    // be replaced by the next key.
    fresh.current = false;
    onChange(value.slice(0, -1));
  };

  /** How many of each chip the total is made of, where that is more than one. */
  const stacked = receipt().chips;
  const times = new Map<number, number>();
  for (const grams of stacked) times.set(grams, (times.get(grams) ?? 0) + 1);

  return (
    <div className="pad">
      {note && <p className="pad__note">{note}</p>}
      {chips && chips.length > 0 && (
        <div className="pad__chips">
          {chips.map((chip) => {
            const count = times.get(chip.grams) ?? 0;
            return (
              <button
                key={`${chip.kind}:${chip.grams}`}
                /* The kind is on the class so the three sources read
                   differently, and in the accessible name so they are *said*
                   differently: a row of six weights with no word between them
                   is a list of numbers, and "45 g, used 12 times" is a reason. */
                className={`pad__chip pad__chip--${chip.kind}${
                  count > 0 || parseDecimal(value) === chip.grams ? " is-on" : ""
                }`}
                /* Said only where it is news. One of a chip is what the chip
                   already says and what the readout is already showing; two of
                   it is the thing a mis-tap would have done quietly. */
                aria-label={count > 1 ? `${chip.detail}, added ${count} times` : chip.detail}
                onClick={() => tapChip(chip)}
              >
                {chip.label}
                {count > 1 && <span className="pad__chip-count"> ×{count}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className="pad__head">
        <span className="pad__name">{name}</span>
        {/* `aria-live` because the figure changes with every key and nothing
            else on screen announces what was typed — the value is not in a
            field a screen reader would read back. */}
        <span className="pad__value num" aria-live="polite">
          {value || "0"}
        </span>
        <span className="pad__unit">{unit}</span>
        {!figures && <span className="pad__kcal num">{kcal ?? "—"}</span>}
      </div>

      {figures && (
        <dl className="pad__figures">
          {figures.map((figure) => (
            <div key={figure.label}>
              <dt>{figure.label}</dt>
              <dd className="num">{figure.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="pad__keys">
        {DIGITS.map((key) => (
          <button key={key} className="pad__key num" onClick={() => press(key)}>
            {key}
          </button>
        ))}
        <button
          className="pad__key pad__key--back"
          onClick={back}
          /* Named for what the press will actually do. The glyph is the same
             either way — it is the back key — but a screen reader offering to
             delete a digit from a weight that is about to lose a whole slice
             would be describing the wrong undo. */
          aria-label={stacked.length > 0 ? "Take back the last portion" : "Delete the last digit"}
        >
          ⌫
        </button>

        {/* Placed explicitly, both of them. Auto-flow puts `Add` wherever the
            eleventh cell lands and then breaks the moment a key spans two rows,
            which is what the two on the right both do. */}
        <button className="pad__go" onClick={onCommit} disabled={disabled || !ready}>
          {action}
        </button>
        {/* A legend, not a toggle. Which unit a food is measured in belongs to
            the food — a packet of olive oil is per 100 ml and always was — so a
            control here could only ever be a switch that changes the meaning of
            the number without changing the number. It says which one is in
            force, and that is the whole job. */}
        {(unit === "g" || unit === "ml") && (
          <span className="pad__unit-key" aria-hidden="true">
            <span className={unit === "g" ? "is-on" : ""}>g</span>
            {" / "}
            <span className={unit === "ml" ? "is-on" : ""}>ml</span>
          </span>
        )}
      </div>
    </div>
  );
}

